import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";

export default function UpgradeDowngrade() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [targetPlan, setTargetPlan] = useState(null);

  // Plans avec leurs caractéristiques
  const plans = {
    standard: {
      name: "Standard",
      price: "29€/mois",
      order: 1,
      features: ["Inventaire illimité", "Gestion artistes & localisation", "Fiscalité de base", "Exports PDF"],
    },
    premium: {
      name: "Premium",
      price: "199€/mois",
      order: 2,
      features: ["Analyse IA avancée", "Recommandations conservation", "Matching successoral", "Veille média"],
    },
    art_advisor: {
      name: "Art Advisor",
      price: "99€/mois",
      order: 3,
      features: ["5 collections clients max", "Multi-portefeuilles", "Rapports personnalisés", "Support prioritaire"],
    },
    art_advisor_premium: {
      name: "Art Advisor Premium",
      price: "199€/mois",
      order: 4,
      features: ["Clients illimités", "IA pour tous les clients", "Co-marquage", "Dashboard agrégé"],
    },
    family_office: {
      name: "Family Office",
      price: "299€/mois",
      order: 5,
      features: ["Portefeuilles illimités", "ERP/PMS", "Conformité", "SLA 99.9%"],
    },
    fondation: {
      name: "Fondation",
      price: "299€/mois",
      order: 6,
      features: ["Inventaire institutionnel illimité", "Prêts & expositions", "Équipes curatoriales", "Vision IA collection"],
    },
  };

  // Définir ce qu'on perd pour chaque downgrade
  const downgradeLosses = {
    "premium->standard": {
      title: "Passage de Premium à Standard",
      warning: "⚠️ Impact important sur vos capacités de gestion",
      losses: [
        "❌ Analyse IA avancée de vos œuvres",
        "❌ Recommandations de conservation personnalisées",
        "❌ Matching successoral automatique",
        "❌ Veille média et alertes marché",
        "❌ Rapports détaillés avancés",
      ],
      consequence: "Vous conserverez l'inventaire de base, mais perdrez tous les outils d'intelligence artificielle et d'analyse avancée.",
    },
    "art_advisor->premium": {
      title: "Passage de Art Advisor à Premium",
      warning: "⚠️ Perte de vos fonctionnalités professionnelles multi-clients",
      losses: [
        "❌ Gestion de vos 5 collections clients",
        "❌ Interface multi-portefeuilles professionnelle",
        "❌ Rapports clients personnalisés avec votre branding",
        "❌ Outils de présentation pour clients",
        "❌ Support prioritaire professionnel",
      ],
      consequence: "ATTENTION : Vos clients actuels perdront l'accès à leurs collections. Vous devrez les informer et ils devront créer leurs propres comptes.",
      flywheel: "💡 Effet Flywheel : En tant qu'Art Advisor, vos clients premium génèrent de la valeur pour votre activité. Cette transition peut impacter votre modèle économique.",
    },
    "art_advisor->standard": {
      title: "Passage de Art Advisor à Standard",
      warning: "🚨 Downgrade majeur avec pertes importantes",
      losses: [
        "❌ TOUTES vos fonctionnalités professionnelles",
        "❌ Gestion multi-clients (5 collections clients)",
        "❌ Interface professionnelle dédiée",
        "❌ Rapports personnalisés",
        "❌ Support prioritaire",
        "❌ Analyse IA avancée",
      ],
      consequence: "ATTENTION CRITIQUE : Vos 5 clients perdront immédiatement l'accès. Cette action peut gravement impacter votre activité professionnelle. Délai de réflexion recommandé : 7 jours.",
      flywheel: "💡 Effet Flywheel interrompu : Vous perdez l'effet multiplicateur de votre réseau client. Vos clients devront trouver une alternative, ce qui peut nuire à votre réputation professionnelle.",
    },
    "art_advisor_premium->art_advisor": {
      title: "Passage de Art Advisor Premium à Art Advisor",
      warning: "⚠️ Réduction des capacités clients",
      losses: [
        "❌ Clients illimités (limitation à 5 clients)",
        "❌ IA complète pour tous vos clients",
        "❌ Co-marquage avancé de vos rapports",
        "❌ Dashboard agrégé multi-clients",
        "❌ Support dédié 24/7",
      ],
      consequence: "Si vous avez plus de 5 clients, vous devrez choisir lesquels conserver. Les autres perdront l'accès immédiatement.",
      flywheel: "💡 Effet Flywheel réduit : Avec la limite de 5 clients, vous ne pourrez plus accepter de nouveaux clients sans en supprimer d'anciens, limitant votre croissance.",
    },
    "art_advisor_premium->premium": {
      title: "Passage de Art Advisor Premium à Premium",
      warning: "🚨 Perte totale de vos fonctionnalités professionnelles",
      losses: [
        "❌ TOUS vos clients (accès illimité perdu)",
        "❌ Gestion multi-portefeuilles",
        "❌ Co-marquage professionnel",
        "❌ Dashboard agrégé",
        "❌ Support dédié professionnel",
      ],
      consequence: "ATTENTION : TOUS vos clients perdront l'accès. Vous redevenez un utilisateur individuel. Cette décision peut avoir un impact majeur sur votre activité.",
      flywheel: "💡 Effet Flywheel arrêté : Vous perdez tout l'écosystème client que vous avez construit. Vos clients devront migrer vers un autre service, ce qui peut nuire durablement à vos relations professionnelles.",
    },
    "art_advisor_premium->standard": {
      title: "Passage de Art Advisor Premium à Standard",
      warning: "🚨 DOWNGRADE EXTRÊME - ACTION DÉCONSEILLÉE",
      losses: [
        "❌ TOUTES fonctionnalités professionnelles",
        "❌ TOUS vos clients (illimités)",
        "❌ TOUTE l'IA avancée",
        "❌ Support professionnel dédié",
        "❌ Outils de gestion pro",
      ],
      consequence: "ALERTE MAXIMALE : Cette action détruit votre configuration professionnelle complète. Impact irréversible sur vos clients et votre activité. Délai de réflexion OBLIGATOIRE : 14 jours.",
      flywheel: "💡 Effet Flywheel complètement détruit : Cette décision met fin à votre activité professionnelle sur Artkiv. Vos clients seront forcés de trouver une alternative immédiatement. Impact potentiel sur votre réputation dans le secteur.",
    },
    "family_office->art_advisor_premium": {
      title: "Passage de Family Office à Art Advisor Premium",
      warning: "⚠️ Réduction des capacités institutionnelles",
      losses: [
        "❌ Portefeuilles illimités institutionnels",
        "❌ Intégrations ERP/PMS sur-mesure",
        "❌ Conformité réglementaire avancée",
        "❌ SLA garanti 99.9%",
        "❌ Account manager dédié",
      ],
      consequence: "Vous conservez les fonctionnalités Art Advisor Premium mais perdez les garanties et intégrations institutionnelles.",
    },
  };

  useEffect(() => {
    const fetchCurrentPlan = async () => {
      if (!user) return;

      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("plan, is_demo")
          .eq("id", user.id)
          .single();

        if (profile) {
          setCurrentPlan(profile.plan);
          setIsDemo(profile.is_demo || false);
        }
      } catch (error) {
        console.error("Erreur:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentPlan();
  }, [user]);

  const handlePlanChange = (newPlan) => {
    const currentOrder = plans[currentPlan]?.order || 0;
    const newOrder = plans[newPlan]?.order || 0;

    // Si c'est un downgrade, afficher la modal d'alerte
    if (newOrder < currentOrder) {
      setTargetPlan(newPlan);
      setShowDowngradeModal(true);
    } else {
      // Upgrade : redirection directe
      if (isDemo) {
        handleDemoChange(newPlan);
      } else {
        navigate(`/pricing?upgrade=${newPlan}`);
      }
    }
  };

  const handleDemoChange = async (newPlan) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ plan: newPlan })
        .eq("id", user.id);

      if (error) throw error;

      alert("Plan démo modifié avec succès !");
      window.location.reload();
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors du changement de plan");
    }
  };

  const confirmDowngrade = () => {
    if (isDemo) {
      handleDemoChange(targetPlan);
    } else {
      navigate(`/pricing?downgrade=${targetPlan}`);
    }
    setShowDowngradeModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin text-4xl">⏳</div>
      </div>
    );
  }

  if (!currentPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-xl mb-4">Aucun plan actif</p>
          <button
            onClick={() => navigate("/pricing")}
            className="px-6 py-3 bg-black text-white rounded-full hover:bg-neutral-800 transition-colors"
          >
            Choisir un plan
          </button>
        </div>
      </div>
    );
  }

  const currentPlanInfo = plans[currentPlan];
  const downgradeLossKey = `${currentPlan}->${targetPlan}`;
  const lossInfo = downgradeLosses[downgradeLossKey];

  return (
    <div className="min-h-screen bg-white py-20 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Plan actuel */}
        <div className="text-center mb-12">
          <h1
            className="text-5xl font-serif mb-4"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Gérer votre abonnement
          </h1>
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-green-50 border-2 border-green-500 rounded-full">
            <span className="text-green-700 font-semibold">Plan actuel :</span>
            <span className="text-2xl font-bold">{currentPlanInfo.name}</span>
            <span className="text-neutral-600">{currentPlanInfo.price}</span>
          </div>
          {isDemo && (
            <div className="mt-4 inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-sm rounded-full">
              Compte démo
            </div>
          )}
        </div>

        {/* Options de changement */}
        <div className="space-y-6">
          <h2 className="text-2xl font-serif mb-6" style={{ fontFamily: "Georgia, serif" }}>
            Changer de plan
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(plans).map(([planId, planInfo]) => {
              if (planId === currentPlan) return null;

              const isUpgrade = planInfo.order > currentPlanInfo.order;
              const isDowngrade = planInfo.order < currentPlanInfo.order;

              return (
                <div
                  key={planId}
                  className="border-2 border-neutral-200 rounded-2xl p-6 hover:border-neutral-400 transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">{planInfo.name}</h3>
                    {isUpgrade && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                        ↗ UPGRADE
                      </span>
                    )}
                    {isDowngrade && (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                        ↘ DOWNGRADE
                      </span>
                    )}
                  </div>

                  <div className="text-2xl font-bold mb-4">{planInfo.price}</div>

                  <ul className="space-y-2 mb-6">
                    {planInfo.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="text-sm text-neutral-600 flex items-start gap-2">
                        <span className="text-black">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePlanChange(planId)}
                    className={`
                      w-full py-3 rounded-full font-semibold transition-all duration-300
                      ${isUpgrade
                        ? "bg-black text-white hover:bg-neutral-800"
                        : "border-2 border-neutral-300 text-neutral-700 hover:border-black hover:text-black"
                      }
                    `}
                  >
                    {isUpgrade ? "Passer à ce plan" : "Revenir à ce plan"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal de confirmation de downgrade */}
      {showDowngradeModal && lossInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-3xl font-serif mb-4" style={{ fontFamily: "Georgia, serif" }}>
              {lossInfo.title}
            </h2>

            <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
              <p className="font-bold text-amber-900 mb-2">{lossInfo.warning}</p>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-3">Vous allez perdre :</h3>
              <ul className="space-y-2">
                {lossInfo.losses.map((loss, idx) => (
                  <li key={idx} className="text-neutral-700 flex items-start gap-2">
                    <span className="text-red-600 font-bold flex-shrink-0">{loss.split(" ")[0]}</span>
                    <span>{loss.substring(loss.indexOf(" ") + 1)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              <p className="text-red-900 font-semibold mb-2">Conséquences :</p>
              <p className="text-red-800">{lossInfo.consequence}</p>
            </div>

            {lossInfo.flywheel && (
              <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                <p className="text-blue-900">{lossInfo.flywheel}</p>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={() => setShowDowngradeModal(false)}
                className="flex-1 py-4 border-2 border-neutral-300 rounded-full font-semibold hover:border-black transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmDowngrade}
                className="flex-1 py-4 bg-red-600 text-white rounded-full font-semibold hover:bg-red-700 transition-colors"
              >
                Confirmer le downgrade
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}